import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Mail, Pencil, RotateCcw, Eye, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  fetchEmailTemplates,
  updateEmailTemplateApi,
  previewEmailTemplate,
  resetEmailTemplate,
} from '@/hooks/useAdmin';
import type { EmailTemplateItem } from '@uniticket/shared';

const EVENT_TYPES = [
  'TICKET_CREATED',
  'STATUS_CHANGED',
  'COMMENT_ADDED',
  'SLA_WARNING',
  'SLA_BREACH',
  'TICKET_ESCALATED',
  'TICKET_RESOLVED',
] as const;

const LANG_LABELS: Record<string, string> = { en: 'EN', fr: 'FR', nl: 'NL' };

export function EmailTemplates() {
  const { t } = useTranslation(['admin', 'common']);
  const [templates, setTemplates] = useState<EmailTemplateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplateItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formSubject, setFormSubject] = useState('');
  const [formGreeting, setFormGreeting] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formCtaLabel, setFormCtaLabel] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchEmailTemplates();
      setTemplates(res.data);
    } catch {
      toast.error(t('admin:emailTemplates.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadData(); }, [loadData]);

  function openEdit(tpl: EmailTemplateItem) {
    setEditingTemplate(tpl);
    setFormSubject(tpl.subject);
    setFormGreeting(tpl.greeting);
    setFormBody(tpl.bodyParagraphs.join('\n'));
    setFormCtaLabel(tpl.ctaLabel);
    setFormIsActive(tpl.isActive);
    setPreviewHtml(null);
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      await updateEmailTemplateApi(editingTemplate.id, {
        subject: formSubject,
        greeting: formGreeting,
        bodyParagraphs: formBody.split('\n').filter((l) => l.trim()),
        ctaLabel: formCtaLabel,
        isActive: formIsActive,
      });
      toast.success(t('admin:emailTemplates.updated'));
      setDialogOpen(false);
      loadData();
    } catch {
      toast.error(t('admin:emailTemplates.saveError'));
    } finally {
      setSaving(false);
    }
  }

  async function handlePreview() {
    if (!editingTemplate) return;
    try {
      await updateEmailTemplateApi(editingTemplate.id, {
        subject: formSubject,
        greeting: formGreeting,
        bodyParagraphs: formBody.split('\n').filter((l) => l.trim()),
        ctaLabel: formCtaLabel,
      });
      const res = await previewEmailTemplate(editingTemplate.id);
      setPreviewHtml(res.data.html);
    } catch {
      toast.error(t('admin:emailTemplates.previewError'));
    }
  }

  async function handleReset(eventType: string, language: string) {
    try {
      await resetEmailTemplate(eventType, language);
      toast.success(t('admin:emailTemplates.resetSuccess'));
      loadData();
      setDialogOpen(false);
    } catch {
      toast.error(t('admin:emailTemplates.resetError'));
    }
  }

  // Group templates by event type
  const grouped = EVENT_TYPES.map((et) => ({
    eventType: et,
    templates: templates.filter((tpl) => tpl.eventType === et),
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-brand-gradient">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{t('admin:emailTemplates.title')}</h1>
            <p className="text-sm text-slate-500">
              {t('admin:emailTemplates.count', { count: templates.length })}
            </p>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
        </div>
      )}

      {/* Template Groups */}
      {!loading && grouped.map(({ eventType, templates: tpls }) => (
        <div key={eventType} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
            <span className="text-sm font-semibold text-slate-700">
              {t(`admin:emailTemplates.events.${eventType}`)}
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {tpls.map((tpl) => (
              <div key={tpl.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/50">
                <div className="flex items-center gap-3 min-w-0">
                  <Badge className="text-xs shrink-0">{LANG_LABELS[tpl.language] || tpl.language}</Badge>
                  <span className="text-sm text-slate-700 truncate">{tpl.subject}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-3">
                  <Badge variant={tpl.isActive ? 'emerald' : 'default'} className="text-[10px]">
                    {tpl.isActive ? t('admin:emailTemplates.active') : t('admin:emailTemplates.inactive')}
                  </Badge>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(tpl)}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Edit Dialog (overlay) */}
      {dialogOpen && editingTemplate && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-brand-purple/10 to-brand-rose/10">
              <h2 className="text-lg font-bold text-slate-900">{t('admin:emailTemplates.editTitle')}</h2>
              <button onClick={() => setDialogOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Badge variant="blue">{t(`admin:emailTemplates.events.${editingTemplate.eventType}`)}</Badge>
                <Badge>{LANG_LABELS[editingTemplate.language]}</Badge>
              </div>

              <div>
                <Label>{t('admin:emailTemplates.fields.subject')}</Label>
                <Input value={formSubject} onChange={(e) => setFormSubject(e.target.value)} />
              </div>

              <div>
                <Label>{t('admin:emailTemplates.fields.greeting')}</Label>
                <Input value={formGreeting} onChange={(e) => setFormGreeting(e.target.value)} />
              </div>

              <div>
                <Label>{t('admin:emailTemplates.fields.body')}</Label>
                <textarea
                  className="w-full rounded-lg border border-slate-200 p-3 text-sm min-h-[120px] focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose outline-none"
                  value={formBody}
                  onChange={(e) => setFormBody(e.target.value)}
                />
                <p className="text-xs text-slate-400 mt-1">{t('admin:emailTemplates.bodyHint')}</p>
              </div>

              <div>
                <Label>{t('admin:emailTemplates.fields.ctaLabel')}</Label>
                <Input value={formCtaLabel} onChange={(e) => setFormCtaLabel(e.target.value)} />
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormIsActive(!formIsActive)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formIsActive ? 'bg-brand-rose' : 'bg-slate-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formIsActive ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <Label className="cursor-pointer" onClick={() => setFormIsActive(!formIsActive)}>
                  {t('admin:emailTemplates.fields.active')}
                </Label>
              </div>

              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs font-semibold text-slate-500 mb-1">{t('admin:emailTemplates.availableVars')}</p>
                <p className="text-xs text-slate-400 font-mono">
                  {'{{recipientName}} {{ticketNumber}} {{subject}} {{ticketType}} {{priority}} {{operatorName}} {{status}} {{oldStatus}} {{newStatus}} {{commentPreview}} {{escalationReason}} {{slaDeadline}}'}
                </p>
              </div>

              {/* Preview */}
              {previewHtml && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-slate-100 text-xs font-semibold text-slate-600">{t('admin:emailTemplates.preview')}</div>
                  <iframe
                    srcDoc={previewHtml}
                    className="w-full h-[400px] border-0"
                    title="Email Preview"
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t bg-slate-50">
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handlePreview}>
                  <Eye className="w-3.5 h-3.5 mr-1.5" />
                  {t('admin:emailTemplates.preview')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleReset(editingTemplate.eventType, editingTemplate.language)}
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  {t('admin:emailTemplates.resetDefault')}
                </Button>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setDialogOpen(false)}>
                  {t('common:actions.cancel')}
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? t('common:actions.saving') : t('common:actions.save')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
